/**
 * Direct Sales Dashboard
 *
 * Leads handled directly by the internal sales team. This page exposes the
 * full field model spelled out in the CRM spec (Lead Info, Source &
 * Marketing, Sales Assignment, Activity, Property Interest, Deal & Closure,
 * SLA & Escalation) and the suggested pipeline:
 *
 *   New Lead → Assigned → Contacted → Qualified → Meeting →
 *   Site Visit → Negotiation → Booking → Closed
 *
 * Data source: the existing `contacts` collection. Fields the spec asks for
 * that aren't yet on the backend render as "—" — the layout is in place so
 * the wiring can land without UI churn.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import HelpInfo from '../components/HelpInfo';
import { toast } from 'sonner';
import {
  Users, Phone, Mail, MapPin, Flame, ThermometerSun, Snowflake,
  Calendar, AlertTriangle, Building2, DollarSign, CheckCircle2, XCircle,
  ChevronRight, Eye, Filter, RefreshCw, Search, Activity, MessageSquare,
} from 'lucide-react';

// Backend mounts the contacts router under /api — without the prefix the
// fetch hits the wrong path and 404s, which is why the table was empty.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pipeline stages in the order the spec defines. Each carries an
// `accent` color used for badges and the funnel stepper. The `exact`
// arrays list verbatim status strings the backend emits for that stage —
// matched FIRST and case-insensitively. The `contains` array is a
// substring fallback for non-standardized variants.
//
// Why two layers: the contacts collection uses statuses like
// 'salesqualifiedlead' and 'marketingqualifiedlead' — both contain the
// substring 'lead', and the original loop matched 'new' first because
// 'new' was checked first. The exact map cuts past that ambiguity.
const PIPELINE_STAGES = [
  { key: 'new',         label: 'New Lead',    color: 'bg-slate-500',
    exact: ['new', 'subscriber', 'active', 'lead', 'pending_qualification', 'conversation initiated'],
    contains: ['subscriber'] },
  { key: 'assigned',    label: 'Assigned',    color: 'bg-blue-500',
    exact: ['assigned'], contains: ['assigned'] },
  { key: 'contacted',   label: 'Contacted',   color: 'bg-cyan-500',
    exact: ['contacted', 'called - no response', 'called', 'no response', 'no_response'],
    contains: ['contacted'] },
  { key: 'qualified',   label: 'Qualified',   color: 'bg-violet-500',
    exact: ['qualified', 'salesqualifiedlead', 'sql', 'marketingqualifiedlead', 'mql'],
    contains: ['qualified'] },
  { key: 'meeting',     label: 'Meeting',     color: 'bg-indigo-500',
    exact: ['meeting'], contains: ['meeting'] },
  { key: 'site_visit',  label: 'Site Visit',  color: 'bg-amber-500',
    exact: ['site_visit', 'visit'], contains: ['site_visit', 'site visit'] },
  { key: 'negotiation', label: 'Negotiation', color: 'bg-orange-500',
    exact: ['negotiation', 'opportunity'], contains: ['negotiation', 'opportunity'] },
  { key: 'booking',     label: 'Booking',     color: 'bg-emerald-500',
    exact: ['booking', 'booked'], contains: ['booking'] },
  { key: 'closed',      label: 'Closed',      color: 'bg-green-600',
    exact: ['customer', 'converted', 'won', 'closed', 'closed_won', 'closed_lost', 'lost'],
    contains: ['closed', 'won', 'lost', 'customer', 'converted'] },
];

// Build an exact lookup map once, lowercased — O(1) status → stage lookup.
const STATUS_TO_STAGE = (() => {
  const m = {};
  for (const st of PIPELINE_STAGES) {
    for (const e of (st.exact || [])) m[e.toLowerCase()] = st.key;
  }
  return m;
})();

// Resolve a contact to a pipeline stage. Business rules:
//   1. Unassigned leads (no owner_id / assigned_to / *_name) are always
//      "New Lead" — they haven't entered the working pipeline yet. We
//      treat ownership as the prerequisite for any post-"new" stage.
//   2. Assigned leads with a "new"-class status (or no status at all)
//      land in "Assigned" — they have an owner but no action yet.
//   3. Everything else falls through to status-driven matching with the
//      same exact-then-longest-substring rules we had before.
//
// Why drive on a contact and not just a status string: a 'qualified' lead
// with no owner is a data anomaly. The matrix would otherwise show an
// unassigned row with leads in Qualified, which the business rule says
// is impossible — those should be visible as "needs assignment" first.
const stageForContact = (contact) => {
  const owner = (contact?.assigned_to_name || contact?.owner_name
    || contact?.assigned_to || contact?.owner_id || '').toString().trim();
  const s = (contact?.status || '').toLowerCase().trim();
  // Rule 1 — no agent → bucket as New Lead (Unassigned). This holds
  // even if the lead's status string says "contacted" — that's a data
  // quality issue and should surface in the Unassigned bucket so a
  // manager assigns ownership before the lead can advance.
  if (!owner) return 'new';
  // Rule 2 — has agent, status is essentially "new" → Assigned.
  if (!s || ['new', 'subscriber', 'active', 'lead', 'pending_qualification', 'conversation initiated'].includes(s)) {
    return 'assigned';
  }
  // Rule 3 — owner is set AND status implies a later stage. Use the
  // exact map first, then longest-substring fallback.
  if (STATUS_TO_STAGE[s]) return STATUS_TO_STAGE[s];
  let bestStage = 'assigned';
  let bestLen = -1;
  for (const st of PIPELINE_STAGES) {
    for (const m of (st.contains || [])) {
      if (s.includes(m) && m.length > bestLen) { bestStage = st.key; bestLen = m.length; }
    }
  }
  return bestStage;
};

// Back-compat wrapper. Existing call sites pass just `status` (which
// loses ownership context); they always end up in 'assigned' or later.
// New code should call `stageForContact` directly.
const stageFor = (statusOrContact) => {
  if (statusOrContact && typeof statusOrContact === 'object') return stageForContact(statusOrContact);
  // Treat a bare status string as "has agent" — same behavior as before.
  return stageForContact({ owner_id: '_assumed', status: statusOrContact });
};

const TEMPERATURE_BADGE = {
  hot:  { label: 'Hot',  icon: Flame,        cls: 'bg-red-100 text-red-700 border-red-300' },
  warm: { label: 'Warm', icon: ThermometerSun, cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  cold: { label: 'Cold', icon: Snowflake,    cls: 'bg-blue-100 text-blue-700 border-blue-300' },
};

const PRIORITY_BADGE = {
  urgent: 'bg-red-100 text-red-700 border-red-300',
  high:   'bg-orange-100 text-orange-700 border-orange-300',
  normal: 'bg-slate-100 text-slate-700 border-slate-300',
  low:    'bg-slate-50 text-slate-500 border-slate-200',
};

// Helper: render a value that may be missing — we want a clear visual
// indication that the field is empty rather than just whitespace.
const fieldValue = (v) => (v === null || v === undefined || v === '') ? '—' : v;

// ============ Sectioned field list ============
// Renders each section heading + a 2-column grid of label/value rows.
// Sections come from the spec exactly so adding a new field later is
// just one extra row in the right section.
const FieldSection = ({ title, icon: Icon, fields }) => (
  <div>
    <h3 className="text-sm font-semibold flex items-center gap-2 mb-2 text-slate-700 dark:text-slate-200">
      {Icon && <Icon size={14} />}
      {title}
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
      {fields.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3 py-1 border-b border-slate-100 dark:border-slate-800 last:border-0">
          <span className="text-slate-500 dark:text-slate-400">{label}</span>
          <span className="font-medium text-slate-900 dark:text-slate-100 text-right truncate" title={String(value ?? '')}>
            {fieldValue(value)}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// ============ Detail dialog ============
// Renders ALL the fields in the spec organized by section. Hidden behind
// the Eye button on each table row to keep the table readable.
const LeadDetailDialog = ({ lead, open, onOpenChange, agentName }) => {
  if (!lead) return null;
  const c = lead;
  const fmt = (d) => d ? new Date(d).toLocaleString() : '';
  const sections = [
    { title: 'Lead Information', icon: Users, fields: [
      ['Lead ID', c.id?.slice(0, 8)],
      ['Lead Name', c.name || c.full_name],
      ['Mobile Number', c.phone || c.mobile],
      ['Email Address', c.email],
      ['Nationality', c.nationality],
      ['Budget', c.budget],
      ['Preferred Location', c.preferred_location],
      ['Property Type', c.property_type],
      ['Bedroom Requirement', c.bedrooms],
      ['Lead Status', c.status],
      ['Lead Priority', c.priority],
      ['Lead Temperature', c.temperature],
    ] },
    { title: 'Source & Marketing', icon: MapPin, fields: [
      ['Lead Source', c.source || c.lead_source],
      ['Campaign Name', c.campaign_name],
      ['UTM Source', c.utm_source],
      ['UTM Medium', c.utm_medium],
      ['UTM Campaign', c.utm_campaign],
      ['Campaign ID', c.campaign_id],
      ['Adset ID', c.adset_id],
      ['Ad ID', c.ad_id],
      ['Landing Page URL', c.landing_page_url],
    ] },
    { title: 'Sales Assignment', icon: Activity, fields: [
      ['Assigned Agent', agentName || c.assigned_to_name || c.owner_name],
      ['Team', c.team],
      ['HOD', c.hod_name],
      ['Assigned Date & Time', fmt(c.assigned_at)],
      ['Response Time', c.first_response_time_minutes ? `${c.first_response_time_minutes} min` : null],
      ['First Contact Date', fmt(c.first_contact_at)],
      ['Last Follow-Up Date', fmt(c.last_follow_up_at || c.last_contacted_at)],
      ['Next Follow-Up Date', fmt(c.next_follow_up_at)],
      ['Number of Follow-Ups', c.follow_up_count],
    ] },
    { title: 'Activity Tracking', icon: MessageSquare, fields: [
      ['Calls Made', c.calls_made_count],
      ['WhatsApp Sent', c.whatsapp_sent_count],
      ['Emails Sent', c.emails_sent_count],
      ['Meeting Scheduled', c.meeting_scheduled_at ? fmt(c.meeting_scheduled_at) : null],
      ['Site Visit Scheduled', c.site_visit_scheduled_at ? fmt(c.site_visit_scheduled_at) : null],
      ['Site Visit Completed', c.site_visit_completed_at ? fmt(c.site_visit_completed_at) : null],
      ['Meeting Notes', c.meeting_notes],
    ] },
    { title: 'Property Interest', icon: Building2, fields: [
      ['Project Name', c.project_name],
      ['Unit Type', c.unit_type],
      ['Unit Number', c.unit_number],
      ['Developer', c.developer],
      ['Property Value', c.property_value],
      ['Payment Plan', c.payment_plan],
      ['Booking Status', c.booking_status],
    ] },
    { title: 'Deal & Closure', icon: DollarSign, fields: [
      ['Deal Stage', c.deal_stage || c.stage],
      ['Expected Closing Date', fmt(c.expected_closing_date)],
      ['Closing Probability', c.closing_probability ? `${c.closing_probability}%` : null],
      ['Deal Value', c.deal_value],
      ['Booking Amount', c.booking_amount],
      ['Commission Amount', c.commission_amount],
      ['Closed Won / Lost', c.closed_status],
      ['Lost Reason', c.lost_reason],
    ] },
    { title: 'SLA & Escalation', icon: AlertTriangle, fields: [
      ['SLA Status', c.sla_status],
      ['Missed SLA Count', c.missed_sla_count],
      ['Escalated to HOD', c.escalated_to_hod ? 'Yes' : 'No'],
      ['Escalated to CEO', c.escalated_to_ceo ? 'Yes' : 'No'],
      ['Escalation Remarks', c.escalation_remarks],
    ] },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} className="text-violet-500" />
            {c.name || c.full_name || 'Lead Detail'}
            {c.temperature && (() => {
              const t = TEMPERATURE_BADGE[(c.temperature || '').toLowerCase()];
              if (!t) return null;
              const TIcon = t.icon;
              return <Badge className={`text-xs border ${t.cls}`}><TIcon size={11} className="mr-1" />{t.label}</Badge>;
            })()}
          </DialogTitle>
          <DialogDescription>
            All fields tracked for this Direct Sales lead. Empty values show as "—" until the source is wired.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="space-y-5">
            {sections.map((s) => <FieldSection key={s.title} {...s} />)}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

// ============ Main page ============
export default function DirectSalesDashboard() {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const [contacts, setContacts] = useState([]);
  // Users map keyed by user id → display name. Used to resolve the
  // assigned-agent label when a contact has only `owner_id` stored
  // (no `assigned_to_name` / `owner_name`). Without this, leads with a
  // bare owner_id show "Assigned" stage but a blank Agent column —
  // confusing because "Assigned" means there IS an agent.
  const [usersById, setUsersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  // Agent filter — "all" / "__unassigned__" / a specific agent name. We
  // key by name (rather than id) because the table only carries the
  // display name, and that's what the dropdown shows anyway. Easier to
  // wire than maintaining a separate user-id roster.
  const [agentFilter, setAgentFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchContacts = () => {
    if (!token) return;
    setLoading(true);
    // No ownership_filter param: let the backend's role-based scope
    // alone decide what's visible. (The backend's `ownership_filter=all`
    // option is misleadingly named — it actually means "my non-actionable
    // leads", which is the *opposite* of what we want for a per-agent
    // pipeline view. CEO/HOD now sit in `is_privileged_role` on the
    // backend so this query returns the whole org's leads to them.)
    axios.get(`${API}/contacts?token=${token}&limit=500`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
        setContacts(list);
      })
      .catch((e) => {
        console.error('[DirectSales] contacts failed:', e);
        // Surface the real reason instead of a silent empty table. Most
        // common cases: 401 (token expired) and 403 (RBAC filtering).
        const msg = e?.response?.data?.detail || e?.response?.data?.error || e.message;
        toast.error(`Couldn't load leads: ${msg}`);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchContacts(); /* eslint-disable-next-line */ }, [token]);

  // One-shot users fetch — used to resolve owner_id → name. The
  // `/users/all` endpoint is lightweight (id + name only). Falls back
  // to the /accounts/users endpoint where /users/all isn't available.
  useEffect(() => {
    if (!token) return;
    const tryUrls = [
      `${API}/users/all?token=${token}`,
      `${API}/accounts/users?token=${token}`,
    ];
    (async () => {
      for (const url of tryUrls) {
        try {
          const res = await axios.get(url);
          const list = res.data?.users || res.data?.data?.users || res.data || [];
          if (!Array.isArray(list) || list.length === 0) continue;
          const map = {};
          list.forEach((u) => {
            const id = u.id || u.user_id;
            const name = u.full_name || u.name || u.email;
            if (id && name) map[id] = name;
          });
          if (Object.keys(map).length) {
            setUsersById(map);
            break;
          }
        } catch { /* try next */ }
      }
    })();
  }, [token]);

  // Resolve a contact's assigned agent to a display name. Walks the
  // candidates in priority order: explicit name field → resolved from
  // id via usersById → short id as last resort. Returns null when truly
  // unassigned so the caller can render "—".
  const agentNameOf = (c) => {
    if (!c) return null;
    const direct = c.assigned_to_name || c.owner_name;
    if (direct) return direct;
    const id = c.assigned_to || c.owner_id;
    if (!id) return null;
    if (usersById[id]) return usersById[id];
    // Falling back to a short id beats showing nothing — at least the
    // manager can see the lead IS assigned and knows it's a name-lookup
    // gap to fix in the data.
    return `Agent ${String(id).slice(0, 8)}`;
  };

  // Apply every non-stage filter (search, temperature, priority, source,
  // agent) without the stage filter. The funnel counts derive from this
  // so each pipeline chip shows "how many of THIS agent's leads in THIS
  // priority are at THIS stage" — i.e. counts respect everything except
  // the stage you're about to click. Without this, counts were always
  // global and the chips behaved like a separate filter that ignored
  // the dropdowns above.
  const nonStageFiltered = useMemo(() => contacts.filter((c) => {
    if (temperatureFilter !== 'all' && (c.temperature || '').toLowerCase() !== temperatureFilter) return false;
    if (priorityFilter !== 'all' && (c.priority || '').toLowerCase() !== priorityFilter) return false;
    if (sourceFilter !== 'all' && c.source !== sourceFilter && c.lead_source !== sourceFilter) return false;
    if (agentFilter !== 'all') {
      const agentName = agentNameOf(c);
      if (agentFilter === '__unassigned__') {
        if (agentName) return false;
      } else if (agentName !== agentFilter) {
        return false;
      }
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = [c.name, c.full_name, c.email, c.phone, c.mobile, c.source, agentNameOf(c)]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contacts, usersById, temperatureFilter, priorityFilter, sourceFilter, agentFilter, search]);

  // Per-stage counts derived from the non-stage-filtered set, so the
  // numbers in the pipeline chips line up with what clicking that chip
  // would actually show.
  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, 0]));
    nonStageFiltered.forEach((c) => { counts[stageFor(c)] = (counts[stageFor(c)] || 0) + 1; });
    return counts;
  }, [nonStageFiltered]);

  // Total for the "All (N)" chip — also reflects the non-stage filters.
  const nonStageTotal = nonStageFiltered.length;

  // Agent × Stage breakdown matrix. Built from `nonStageFiltered` so all
  // non-stage filters (search, temperature, priority, source) flow into
  // the per-agent numbers — but NOT the agent or stage filters, since
  // those would just trivially collapse the matrix.
  //
  // Built from the same pre-filtered set as the chips, then re-applied
  // *without* the agent filter so an HOD can see every agent at once
  // without first clearing the agent dropdown. The user explicitly asked
  // for "each agent's stages" — so the matrix should never hide an agent
  // because of the dropdown.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agentMatrix = useMemo(() => {
    const baseSet = contacts.filter((c) => {
      // Same shape as nonStageFiltered but DROPS the agent filter — the
      // matrix is "for each agent in the visible scope".
      if (temperatureFilter !== 'all' && (c.temperature || '').toLowerCase() !== temperatureFilter) return false;
      if (priorityFilter !== 'all' && (c.priority || '').toLowerCase() !== priorityFilter) return false;
      if (sourceFilter !== 'all' && c.source !== sourceFilter && c.lead_source !== sourceFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = [c.name, c.full_name, c.email, c.phone, c.mobile, c.source, agentNameOf(c)]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    // Group: { agentName: { stageKey: count, ...; total } }
    const rows = new Map();
    const ensure = (name) => {
      if (!rows.has(name)) {
        const init = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, 0]));
        rows.set(name, { ...init, total: 0 });
      }
      return rows.get(name);
    };
    for (const c of baseSet) {
      const name = agentNameOf(c) || '— Unassigned';
      const stage = stageFor(c);
      const row = ensure(name);
      row[stage] = (row[stage] || 0) + 1;
      row.total += 1;
    }
    // Sort: unassigned first, then by total desc, then alphabetical
    const list = Array.from(rows.entries())
      .map(([name, counts]) => ({ name, ...counts }))
      .sort((a, b) => {
        if (a.name === '— Unassigned') return -1;
        if (b.name === '— Unassigned') return 1;
        if (b.total !== a.total) return b.total - a.total;
        return a.name.localeCompare(b.name);
      });
    return list;
  }, [contacts, usersById, temperatureFilter, priorityFilter, sourceFilter, search]);

  // Distinct lead sources for the filter dropdown — auto-built from
  // whatever the backend returns so we don't hardcode a list that drifts.
  const sourceOptions = useMemo(() => {
    const set = new Set();
    contacts.forEach((c) => { if (c.source) set.add(c.source); else if (c.lead_source) set.add(c.lead_source); });
    return Array.from(set).sort();
  }, [contacts]);

  // Distinct assigned agents. Built from the same data the table renders
  // so the dropdown can never get out of sync with reality. We also count
  // how many leads each agent has so the option label can hint at load.
  // Uses `agentNameOf` so owner-id-only leads resolve through the users
  // map and don't get counted as "unassigned".
  const agentOptions = useMemo(() => {
    const counts = new Map();
    let unassigned = 0;
    contacts.forEach((c) => {
      const name = agentNameOf(c);
      if (!name) { unassigned += 1; return; }
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    const list = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { list, unassigned };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, usersById]);

  // Final filtered list = non-stage filtered set ∩ stage filter. Doing it
  // this way guarantees the counts in the chips above and the rows in the
  // table below stay perfectly in sync — they share the same source.
  const filtered = useMemo(() => {
    if (stageFilter === 'all') return nonStageFiltered;
    return nonStageFiltered.filter((c) => stageFor(c) === stageFilter);
  }, [nonStageFiltered, stageFilter]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`}>
      <div className="max-w-[1600px] mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
              <Users className="text-violet-500" size={28} />
              Direct Sales Dashboard
              <HelpInfo
                align="inline"
                title="How to use the Direct Sales Dashboard"
                steps={[
                  <>The summary tiles count your internal-team leads; the <strong>Pipeline</strong> strip below shows how many sit at each stage — click a stage chip to filter the table to just those leads.</>,
                  <>A lead's stage is derived from its status and ownership: an unassigned lead always shows as <em>New Lead</em> until an agent owns it.</>,
                  <>The <strong>Agent Performance</strong> matrix breaks leads down by agent and stage — click an agent or a stage cell to drill into that bucket.</>,
                  <>Use the search and the temperature / priority / source / agent filters to narrow the list; the chip counts update to match.</>,
                  <>Click the eye icon on any row for the full lead detail.</>,
                ]}
                tip="Work the Unassigned bucket first — a lead can't progress past New Lead until someone owns it."
              />
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Leads handled directly by your internal sales team — full lifecycle from new lead to closing.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchContacts} disabled={loading}>
            <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Pipeline stages — each tile is a filter button */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Pipeline</CardTitle>
            <CardDescription>
              New Lead → Assigned → Contacted → Qualified → Meeting → Site Visit → Negotiation → Booking → Closed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setStageFilter('all')}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                  stageFilter === 'all'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                }`}
              >
                All ({nonStageTotal})
              </button>
              {PIPELINE_STAGES.map((s, idx) => (
                <React.Fragment key={s.key}>
                  <button
                    type="button"
                    onClick={() => setStageFilter(s.key)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      stageFilter === s.key
                        ? `${s.color} text-white border-transparent ring-2 ring-offset-2 ring-slate-300`
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {s.label}
                    <span className={`ml-2 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded text-xs ${
                      stageFilter === s.key ? 'bg-white/20' : 'bg-slate-100 text-slate-700'
                    }`}>{stageCounts[s.key] || 0}</span>
                  </button>
                  {idx < PIPELINE_STAGES.length - 1 && (
                    <ChevronRight size={14} className="text-slate-300 hidden lg:inline-block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={16} className="text-slate-400" />
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, phone, source…"
                  className="pl-9"
                />
              </div>
              <Select value={temperatureFilter} onValueChange={setTemperatureFilter}>
                <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Temperature" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All temperatures</SelectItem>
                  <SelectItem value="hot">Hot</SelectItem>
                  <SelectItem value="warm">Warm</SelectItem>
                  <SelectItem value="cold">Cold</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {sourceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {/* Agent filter — built from the same data the table shows.
                  Each option carries its lead count so a manager can spot
                  workload at a glance. */}
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger className="h-9 w-[200px]"><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All agents</SelectItem>
                  {agentOptions.unassigned > 0 && (
                    <SelectItem value="__unassigned__">
                      Unassigned ({agentOptions.unassigned})
                    </SelectItem>
                  )}
                  {agentOptions.list.map((a) => (
                    <SelectItem key={a.name} value={a.name}>
                      {a.name} ({a.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(agentFilter !== 'all' || temperatureFilter !== 'all' || priorityFilter !== 'all' || sourceFilter !== 'all' || stageFilter !== 'all' || search) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setAgentFilter('all'); setTemperatureFilter('all'); setPriorityFilter('all');
                    setSourceFilter('all'); setStageFilter('all'); setSearch('');
                  }}
                >
                  Clear
                </Button>
              )}
              <span className="ml-auto text-xs text-slate-500">
                Showing {filtered.length} of {contacts.length}
                {nonStageTotal !== contacts.length && (
                  <span className="ml-1 text-slate-400">({nonStageTotal} after filters)</span>
                )}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Agent Performance Matrix — shows every agent's leads broken
            out by pipeline stage. Lets a manager see "who is handling what"
            and "what's closed" in a single glance. Clicking an agent's
            row sets the agent filter so the table below narrows to that
            agent; clicking a stage cell narrows BOTH the agent and the
            stage so you can drill straight into the leads in that cell.
            Only shown when there are at least 2 distinct agents — with
            one agent the table below already tells the whole story. */}
        {agentMatrix.length >= 2 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Agent Performance</CardTitle>
              <CardDescription>
                Per-agent lead distribution across the pipeline. Click an agent's name to filter the table below; click any stage cell to drill into that specific bucket.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-2 px-3 sticky left-0 bg-white dark:bg-[#0a0a0f] z-10">Agent</th>
                      {PIPELINE_STAGES.map((s) => (
                        <th key={s.key} className="text-center py-2 px-2 whitespace-nowrap">{s.label}</th>
                      ))}
                      <th className="text-center py-2 px-3 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentMatrix.map((row) => {
                      const isUnassigned = row.name === '— Unassigned';
                      return (
                        <tr
                          key={row.name}
                          className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${
                            isUnassigned ? 'text-slate-500 italic' : ''
                          }`}
                        >
                          <td className="py-2 px-3 font-medium sticky left-0 bg-white dark:bg-[#0a0a0f] z-10">
                            <button
                              type="button"
                              className="text-left hover:underline decoration-dotted"
                              onClick={() => {
                                setAgentFilter(isUnassigned ? '__unassigned__' : row.name);
                                setStageFilter('all');
                              }}
                              title="Filter table to this agent"
                            >
                              {isUnassigned ? 'Unassigned' : row.name}
                            </button>
                          </td>
                          {PIPELINE_STAGES.map((s) => {
                            const count = row[s.key] || 0;
                            return (
                              <td key={s.key} className="text-center py-2 px-2">
                                {count > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAgentFilter(isUnassigned ? '__unassigned__' : row.name);
                                      setStageFilter(s.key);
                                    }}
                                    className={`inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded text-xs font-medium ${
                                      s.key === 'closed'
                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                    title={`See ${isUnassigned ? 'unassigned' : row.name}'s ${s.label.toLowerCase()} leads`}
                                  >
                                    {count}
                                  </button>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="text-center py-2 px-3 font-bold">{row.total}</td>
                        </tr>
                      );
                    })}
                    {/* Totals row — column sums so a manager can see the
                        team-wide spread at the bottom in one place. */}
                    <tr className="border-t-2 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 font-semibold">
                      <td className="py-2 px-3 sticky left-0 bg-slate-50 dark:bg-slate-800/40 z-10">Team Total</td>
                      {PIPELINE_STAGES.map((s) => {
                        const sum = agentMatrix.reduce((acc, r) => acc + (r[s.key] || 0), 0);
                        return (
                          <td key={s.key} className="text-center py-2 px-2">
                            {sum > 0 ? sum : <span className="text-slate-300 font-normal">—</span>}
                          </td>
                        );
                      })}
                      <td className="text-center py-2 px-3">
                        {agentMatrix.reduce((acc, r) => acc + r.total, 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leads table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Leads</CardTitle>
            <CardDescription>Click the eye icon to see every tracked field for a lead.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-slate-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-slate-500">
                No leads match these filters.
              </div>
            ) : (
              <ScrollArea className="h-[520px]">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-2 px-3">Lead</th>
                      <th className="text-left py-2 px-3">Contact</th>
                      <th className="text-left py-2 px-3">Source</th>
                      <th className="text-center py-2 px-3">Temp</th>
                      <th className="text-center py-2 px-3">Priority</th>
                      <th className="text-left py-2 px-3">Stage</th>
                      <th className="text-left py-2 px-3">Assigned</th>
                      <th className="text-center py-2 px-3 w-12">View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c) => {
                      const stage = PIPELINE_STAGES.find((s) => s.key === stageFor(c));
                      const temp = TEMPERATURE_BADGE[(c.temperature || '').toLowerCase()];
                      return (
                        <tr key={c.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                          <td className="py-2 px-3 font-medium">{c.name || c.full_name || '—'}</td>
                          <td className="py-2 px-3 text-slate-600 dark:text-slate-300">
                            <div className="flex items-center gap-1 text-xs"><Phone size={11} />{fieldValue(c.phone || c.mobile)}</div>
                            <div className="flex items-center gap-1 text-xs"><Mail size={11} />{fieldValue(c.email)}</div>
                          </td>
                          <td className="py-2 px-3 text-slate-600 dark:text-slate-300">{fieldValue(c.source || c.lead_source)}</td>
                          <td className="py-2 px-3 text-center">
                            {temp ? (
                              <Badge className={`text-xs border ${temp.cls}`}>
                                <temp.icon size={11} className="mr-1" />{temp.label}
                              </Badge>
                            ) : '—'}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Badge className={`text-xs border ${PRIORITY_BADGE[(c.priority || 'normal').toLowerCase()] || PRIORITY_BADGE.normal}`}>
                              {c.priority || 'normal'}
                            </Badge>
                          </td>
                          <td className="py-2 px-3">
                            {stage && (
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs text-white ${stage.color}`}>
                                {stage.label}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-slate-600 dark:text-slate-300">
                            {fieldValue(agentNameOf(c))}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => { setSelected(c); setDetailOpen(true); }}
                              title="View all fields"
                            >
                              <Eye size={14} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <LeadDetailDialog
          lead={selected}
          agentName={selected ? agentNameOf(selected) : null}
          open={detailOpen}
          onOpenChange={setDetailOpen}
        />
      </div>
    </div>
  );
}
